import { ArticleBaseSchema } from "@acme/db/schema";
import { useAppForm } from "@acme/ui/form";
import { NotFound } from "@acme/ui/not-found";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  ErrorComponent,
  useRouter,
} from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod/v4";
import { ArticleForm } from "~/components/blog/form";
import { queryKeys } from "~/lib/query-keys";
import { $getArticleById, $updateArticle } from "~/lib/server/blog";

export const Route = createFileRoute("/(dashboard)/blog/$articleId/edit")({
  component: ArticlesEditPage,
  loader: async ({ params: { articleId }, context: { queryClient } }) => {
    const data = await queryClient.ensureQueryData({
      queryKey: queryKeys.blog.byId(articleId),
      queryFn: () => $getArticleById({ data: { id: articleId } }),
    });

    return { title: data?.title };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `Edit Article: ${loaderData?.title} | Dashboard` }],
  }),
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  notFoundComponent: () => <NotFound>Project not found</NotFound>,
});

function ArticlesEditPage() {
  const { articleId } = Route.useParams();

  const article = useSuspenseQuery({
    queryKey: queryKeys.blog.byId(articleId),
    queryFn: () => $getArticleById({ data: { id: articleId } }),
  });

  const router = useRouter();
  const queryClient = useQueryClient();

  const updateArticleMutation = useMutation({
    mutationFn: (data: z.infer<typeof ArticleBaseSchema> & { id: string }) =>
      $updateArticle({ data }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.blog.all });
      toast.success("Article updated successfully");
      form.reset();
      router.navigate({ to: "/blog" });
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      toast.error(
        `Failed to create article: ${
          errorMessage.includes("validation")
            ? "Please check your form inputs"
            : "Server error. Please try again later."
        }`
      );
    },
  });

  const handleFormSubmit = (data: z.infer<typeof ArticleBaseSchema>) => {
    updateArticleMutation.mutate({
      ...data,
      id: article.data?.id ?? "",
    });
  };

  const form = useAppForm({
    defaultValues: {
      title: article.data?.title ?? "",
      slug: article.data?.slug ?? "",
      description: article.data?.description ?? "",
      content: article.data?.content ?? "",
      thumbnail: "",
      isDraft: article.data?.isDraft ?? false,
      tags: article.data?.tags ?? [],
    },
    validators: {
      onChange: ArticleBaseSchema,
    },
    onSubmit: ({ value }) => {
      handleFormSubmit(value);
    },
  });

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
        <div>
          <h2 className="font-bold text-2xl tracking-tight">Edit Article</h2>
          <p className="text-muted-foreground">Edit an article here.</p>
        </div>
      </div>
      <div className="py-4">
        <form.AppForm>
          <form
            className="space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <ArticleForm article={article.data} form={form} />
          </form>
        </form.AppForm>
      </div>
    </>
  );
}
